import { FieldMap } from '../../../types/forms/fields'
import { encounterTypeName } from '../constants/encounterTypes'
import { Encounter, EncounterSections, SectionGenerator } from '../types/encounter'
import { EncounterTypeConfig } from '../types/encounterConfig'
import { EncounterType } from '../types/encounterType'
import { getEducationSection } from './education'
import { encounterExamField } from './encounter'
import { getPelvicExamSection } from './pelvic'
import { getGeneralPhysicalExamSection, physicalExamField } from './physical'
import { postpartumExamField } from './postpartum'
import { externalRecordsReviewedField } from './prenatal'
import { encounterDateField } from './shared'
import { getUrineExamSection } from './urine'
import { getSectionValidationField } from './validation'
import { wellWomanExamField } from './wellWoman'

export * from './admissionSection'
export * from './general'
export * from './modifierSuggestions'
export * from './pelvic'
export * from './physical'
export * from './postpartum'
export * from './prenatal'
export * from './screenings'
export * from './shared'
export * from './ultrasound'
export * from './urine'
export * from './validation'
export * from './visitLocation'
export * from './vitals'

const getHasObEncounters = (assessmentId: string | null, patientEncounters: Encounter[]): boolean =>
  patientEncounters.some(
    enc =>
      enc.assessmentId === assessmentId &&
      (enc.type === EncounterType.ANTEPARTUM ||
        enc.type === EncounterType.POSTPARTUM ||
        enc.type === EncounterType.BIRTHING_CENTER_ADMISSION),
  )

const getRoutineAntepartumExamSections = (): SectionGenerator[] => [
  (assessmentId, patientEncounters) => {
    const hasObEncounters = getHasObEncounters(assessmentId, patientEncounters)
    const sections: EncounterSections = [
      // { type: 'pregnancy-header' },
      // §2.16 / M.6: pre-visit-contact is no longer included by default.
      // Providers opt in via ContactReviewBanner "Add to this note" or the
      // "+ Add pre-visit contact" link in EncounterFormBody.
      { type: 'reason-for-visit' },
      { type: 'visit-location', isRoutineAntepartum: true },
      { type: 'allergies' },
      { type: 'notes' },
    ]

    if (!hasObEncounters) {
      sections.splice(1, 0, { type: 'first-ob-visit' })
    }
    return sections
  },
  () => [
    { type: 'vitals' },
    { type: 'prenatal-exam' }, // §2.4–2.5: interval history + fetal exam — tier-validated section
    { type: 'medications-supplements' }, // §2.5: Tier 1 confirm-from-chart — routine only
    { type: 'physical-exam' as const }, // §2.6: accordion card with defaults + cervical exam
    { type: 'pelvic-exam' as const }, // §2.6: accordion card
    { type: 'labs-diagnostics' as const }, // §2.7: ordered labs, review, GBS, GCT/GTT, other labs
    { type: 'field', field: externalRecordsReviewedField },
    { type: 'assessment-plan' },
    { type: 'patient-education' as const }, // §2.8.2: GA-triggered checklist — TIER 3
    { type: 'dv-ipv-screening' as const }, // §2.8.3
    { type: 'depression-screening' as const }, // §2.8.4
    { type: 'substance-use-counseling' as const }, // §2.8.5
    { type: 'mdm' }, // §2.10.1
    { type: 'icd10-selection' },
  ],
]

const getNonRoutineAntepartumExamSections = (): SectionGenerator[] => [
  () => [
    // { type: 'pregnancy-header' },
    // §2.16 / M.6: pre-visit-contact omitted by default — opt-in only.
    { type: 'visit-location' },
    { type: 'notes' },
  ],
  // §2.1.4: Non-routine uses SOAP Note mode. Chief complaint lives in SOAP S section —
  // no separate chiefComplaintFieldMap step. nonRoutinePrenatalSection handles AI suggestions
  // and clinical relationship to pregnancy. soap-note replaces §§2.4, 2.5, 2.6, and 2.8.
  // §2.1 Telehealth: general-illness-exam is in-person only — replaced by remote-assessment
  // when modality is telehealth (formData available at step transition time).
  (assessmentId, patientEncounters, formData) => {
    const hasObEncounters = getHasObEncounters(assessmentId, patientEncounters)
    const modality = (formData as any)?.visitLocation?.modality
    const isTelehealth = modality === 'telehealth-video' || modality === 'telehealth-audio-only'
    const sections: EncounterSections = [
      { type: 'clinical-rationale' },
      { type: 'allergies' },
      { type: 'soap-note' },
      isTelehealth
        ? { type: 'remote-assessment' as const }
        : { type: 'general-illness-exam' as const },
      { type: 'field', field: externalRecordsReviewedField },
      // §M.5: assessment-plan removed — SOAP note's assessment and plan fields (Tier 1)
      // already cover this documentation requirement for non-routine antepartum visits.
      { type: 'mdm' }, // §2.10.1
      { type: 'icd10-selection' },
    ]
    if (!hasObEncounters) {
      sections.splice(0, 0, { type: 'first-ob-visit' })
    }
    return sections
  },
]

const getAntepartumExamSections = (
  config: EncounterTypeConfig<EncounterType.ANTEPARTUM> | null,
): SectionGenerator[] =>
  config?.visitType === 'non-routine'
    ? getNonRoutineAntepartumExamSections()
    : getRoutineAntepartumExamSections()

export const encounterFields: {
  [K in EncounterType]: (config: EncounterTypeConfig<K> | null) => SectionGenerator[]
} = {
  [EncounterType.PHYSICAL]: () => [
    () => [{ type: 'visit-location' }, { type: 'notes' }],
    () => [
      { type: 'vitals' },
      { type: 'field', field: physicalExamField },
      { type: 'risk-assessment' },
    ],
  ],
  [EncounterType.ENCOUNTER]: () => [
    () => [{ type: 'visit-location' }, { type: 'notes' }],
    () => [
      { type: 'vitals' },
      { type: 'field', field: encounterExamField },
      getGeneralPhysicalExamSection(true),
      getPelvicExamSection(true),
      getUrineExamSection(true),
      { type: 'review-of-systems' },
      { type: 'risk-assessment' },
    ],
  ],
  [EncounterType.WELL_WOMAN]: () => [
    () => [{ type: 'visit-location' }, { type: 'notes' }],
    () => [
      { type: 'vitals', optional: true },
      { type: 'field', field: wellWomanExamField },
      getGeneralPhysicalExamSection(true),
      getEducationSection(true),
      { type: 'review-of-systems' },
      { type: 'risk-assessment' },
    ],
  ],
  // §1.2.1 Pre-Conception Consultation — COMPLIANCE: no OB sections, no vitals.
  // ICD-10 O-code filter is enforced in ICD10SelectionInput when encounterType === PRE_CONCEPTION.
  // TH modifier guard is enforced in modifierSuggestions.ts.
  [EncounterType.PRE_CONCEPTION]: () => [
    () => [{ type: 'visit-location' as const }, { type: 'notes' as const }],
    () => [{ type: 'assessment-plan' as const }, { type: 'icd10-selection' as const }],
  ],
  [EncounterType.ANTEPARTUM]: config => getAntepartumExamSections(config),
  [EncounterType.POSTPARTUM]: () => [
    (assessmentId, patientEncounters) => {
      const hasObEncounters = getHasObEncounters(assessmentId, patientEncounters)
      const sections: EncounterSections = [{ type: 'visit-location' }, { type: 'notes' }]
      if (!hasObEncounters) {
        sections.push({ type: 'first-ob-visit' })
      }
      return sections
    },
    () => [
      { type: 'vitals' },
      { type: 'field', field: postpartumExamField },
      { type: 'risk-assessment' },
      { type: 'patient-education' as const }, // §2.8.2: postpartum window — TIER 3
      { type: 'depression-screening' as const }, // §2.8.4: TIER 1 at postpartum
    ],
  ],
  [EncounterType.BIRTHING_CENTER_ADMISSION]: () => [
    // §3.1: Pregnancy header auto-computes GA from EDD and writes data.gestationalAge — same
    // pattern as antepartum. Notes section follows for any additional admission context.
    // () => [{ type: 'pregnancy-header' as const }, { type: 'notes' as const }],
    () => [{ type: 'notes' as const }],
    () => [{ type: 'birthing-center-admission' as const }],
  ],
  [EncounterType.ULTRASOUND]: config => [
    () => [{ type: 'ultrasound-header' as const }],
    () => [
      {
        type: 'ultrasound-findings' as const,
        scenario: config?.scenarioType ?? 'provider-pocus',
      },
      { type: 'icd10-selection' as const },
    ],
  ],
}

export const getEncounterValidationField = <T extends EncounterType>(
  assessmentId: string | null,
  patientEncounters: Encounter[],
  type: T,
  config: EncounterTypeConfig<T> | null,
): FieldMap => ({
  name: `${encounterTypeName[type]} Exam`,
  children: {
    date: encounterDateField,
    data: {
      name: 'Data',
      children: encounterFields[type](config)
        .reduce<EncounterSections>(
          (acc, gen) => [...acc, ...gen(assessmentId, patientEncounters, {}, acc)],
          [],
        )
        .reduce((acc, section) => {
          const sectionField = getSectionValidationField[section.type](section as any)
          if (!sectionField) return acc
          return { ...acc, ...sectionField.children }
        }, {}),
    },
  },
})
